import {
  cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import './quotes.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { statusVariant } from '../../../components/statusPill';
import { useNotify } from '../../../components/notifications';
import { focusFirstFormControl } from '../../../hooks/useAutoFocusFirstControl';
import { guardedExit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { fmtLocalDate } from '../../../utils/datetime';
import { ALL_NAV_ACTIONS, type NavActions } from '../../../layouts/navPermissions';
import PreLoginDocsPanel, { type DocsOwner } from '../../settings/preLoginSettings/PreLoginDocsPanel';
import {
  calculateQuote, createQuote, downloadQuoteReport, fetchContacts, fetchProjects,
  fetchQuote, fetchQuoteLookups, reportErrorMessage, updateQuote, validateQuote,
} from './detailApi';
import {
  CHARGE_TYPE, blankQuote,
  type AnalysisRowInput, type ContactOption, type ItemChargeCandidate, type ItemRowInput,
  type PartyOption, type PriceCodeCandidate, type ProjectOption, type QuoteAnalysisRow,
  type QuoteDetail, type QuoteInput, type QuoteItemRow, type QuoteLookups, type NamedLookupKind,
} from './detailTypes';
import { ItemChargePickerDialog } from './ItemChargePickerDialog';
import { PriceCodePickerDialog, candidateTestKey } from './PriceCodePickerDialog';
import { QuoteGridLookup } from './QuoteGridLookup';
import { QuoteValidateDialog } from './QuoteValidateDialog';
import { TatPickerDialog, SampleMatrixPickerDialog } from './QuoteCellPickers';
import { CellContextMenu, canBulkCopy } from '../../../components/gridEdit/CellContextMenu';
import { QuoteNamedLookupDialog } from './QuoteNamedLookupDialog';
import { ClientDetailModal } from '../../sampling/ClientDetailModal';
import { ContactDetailModal } from '../../sampling/ContactDetailModal';
import { AiCombo } from '../../../components/AiCombo';
import { ExportMenu } from '../../../components/AppToolbar/ExportMenu';
import { gridExportMatrix } from '../../../components/AppDataGrid';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes — the Open Quote DETAIL view.
// Port of legacy ALPACA `CRMQuotes_DetailView` (BTLIMS.Web/Model.xafml :11543),
// caption "Open Quote". The list is REPLACED by this page, never a popup
// (standing rule 12).
//
// LAYOUT — the legacy groups, in the legacy order, two columns each:
//   Summary · Totals · Contacts · Bill To Address · [Same Address] ·
//   Report to Address · Account Management
// then the tabs Analysis Pricing · Item Charge.
//
// NO PRICE IS COMPUTED HERE. Every number in the two grids and in Totals comes
// back from `POST /quotes/calculate`, which runs the same code as the save
// (CLAUDE.md §1 — the UI is replaceable, so a quoted price must not depend on
// this component having run). This page only collects what a person types:
// quantities, discounts, descriptions and remarks.
//
// ACTION BAR, top and bottom, identical, in the reference form's order and
// wording: Save · Save and Close · Save and New · Preview · | · Cancel. Legacy
// registers all three save actions on this view (QuoteViewController
// :2676-2678), so the set is parity as well as rule 12. No Delete here — the
// standard keeps Delete out of an edit view; a quote is deleted from the list.
//
// LAYOUT (2026-10-13, owner: "improve the design with our rules"). Rebuilt on
// the SHARED detail-view classes from SettingsDetailPage.css — `.sd-wrap` /
// `.sd-head` / `.sd-actionbar` / `.sd-body` / `.sd-section-h` / `.sd-grid--side`
// / `.sd-assoc-tabbar` — after comparing it side by side with the reference
// form, Settings ▸ Inventory Management ▸ Items (standing rule 12). The page
// had its own cards, buttons and tabs before; now only the BODY scrolls and
// both action bars stay in view.
//
// ⚠️ RULE 12 DEVIATION, deliberate and flagged: bespoke rather than the shared
// <SettingsDetailPage>, because that component has no slot for two editable
// child grids with their own pickers, nor for totals that are recomputed by
// the server as the grids change. Same precedent as ReagentPrepDetailPage and
// ADR-0015's SdmsSequenceSetupPage. It still composes the shared AppDataGrid /
// AppDialog / useSaveFlow / useConfirm / useNotify / useHotkeys, and the shared
// detail-view stylesheet supplies its whole layout.
//
// NOT IN THIS WAVE: the Submit / Validate / History actions, changing a row's TAT (rush pricing) and per-parameter
// pricing. See docs/03-Code-Change-Documentation/Migration-Log.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Open Quotes';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

type Tab = 'analysis' | 'items' | 'attachment' | 'note';

interface Props {
  /** null = a new quote. */
  id: string | null;
  onClose: () => void;
  /** Save & New — start another blank quote. */
  onNew: () => void;
  /** Save on a new quote — carry on editing it under its real id. */
  onCreated: (id: string) => void;
  /** Something was saved or deleted; the list behind should reload. */
  onChanged: () => void;
  /** Prev / next record arrows (BO UI Standard §4) — the current page's ids. */
  siblingIds?: string[];
  onNavigate?: (id: string) => void;
  /** The signed-in user's MAC rights for this form. Write actions the user may not
   *  perform are HIDDEN (never greyed); a user without the right sees a read-only
   *  quote. Defaults to full rights (admins / forms with no MAC level). */
  perm?: NavActions;
}

/** The shared Attachment | Notes panel, pointed at a quote. */
const QUOTE_DOCS: DocsOwner = {
  apiBase: '/api/v1/quotes',
  formName: 'Open Quotes',
  businessObject: 'Open Quote',
  exportPrefix: 'Quote',
  noteTabLabel: 'Notes',
  countFromRows: true,
  // Legacy CRMQuotes_Attachments_ListView / CRMQuotes_Note_ListView: New · Edit ·
  // Delete · Export to, and the legacy Notes columns (owner screenshot 2026-10-20).
  legacyListBar: true,
};

export default function QuoteDetailPage({
  id, onClose, onNew, onCreated, onChanged, siblingIds = [], onNavigate, perm = ALL_NAV_ACTIONS,
}: Props) {
  const notify      = useNotify();
  const { prompt }  = useConfirm();
  const runSave     = useSaveFlow();
  const isNew       = id === null;
  // Focus the first field once the form is showing (standing rule 14).
  const pageRef = useRef<HTMLDivElement>(null);

  const [quote, setQuote] = useState<QuoteDetail>(blankQuote);
  const [rows, setRows]   = useState<QuoteAnalysisRow[]>([]);
  const [items, setItems] = useState<QuoteItemRow[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [dirty, setDirty]     = useState(false);
  /** Won (2) or Lost (7): legacy opens a validated quote only in its read-only
   *  "Quote Reviewed" views (AllowEdit="False", CollectionsEditMode="View"), and
   *  the API refuses any write to it (QuoteRules.IsLocked). */
  const statusLocked = !isNew && (quote.storedStatus === 2 || quote.storedStatus === 7);
  /** MAC: a new quote needs Create, a stored one Write. Without it the quote is
   *  view-only — the save actions are hidden and every field is disabled. */
  const mayEdit = isNew ? perm.create : perm.write;
  const locked = statusLocked || !mayEdit;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const [tab, setTab]         = useState<Tab>('analysis');
  // Reported by the documents panel once it has loaded, for the tab captions.
  const [docCounts, setDocCounts] = useState({ attachments: 0, notes: 0 });
  // The FORM-field variant on purpose: the pricing grid focuses its own first
  // row as its data arrives, and the generic hook would then stand down. This
  // one ignores grids, so the form opens on its top field; it still never takes
  // focus from a field the user is already in.
  useEffect(() => {
    if (loading || !pageRef.current) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable)) return;
    focusFirstFormControl(pageRef.current);
  }, [loading]);

  // The form is wide — two field columns and two pricing grids — so opening it
  // folds the left navigation away (owner request 2026-10-20). The SHARED
  // ai:nav-collapse / ai:nav-restore pair QC Batch, Prep Batch and PLM use: the
  // shell puts the navigation back as the user had it when the form closes.
  // Dispatched again on the next frame for a direct load, where this effect can
  // run before the shell is listening.
  useEffect(() => {
    window.dispatchEvent(new Event('ai:nav-collapse'));
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('ai:nav-collapse')));
    return () => {
      cancelAnimationFrame(raf);
      window.dispatchEvent(new Event('ai:nav-restore'));
    };
  }, []);

  const [lookups, setLookups]   = useState<QuoteLookups | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [contacts, setContacts] = useState<ContactOption[]>([]);

  const [pickCodes, setPickCodes] = useState(false);
  const [pickItems, setPickItems] = useState(false);
  const [rowSel, setRowSel]   = useState<Set<string | number>>(new Set());
  const [itemSel, setItemSel] = useState<Set<string | number>>(new Set());

  /** Final Amounts the user TYPED on item rows, by row id. Kept apart from the
   *  rows because the server works the discount back from them (legacy
   *  :8633-8648) and must be told which ones were typed. */
  const [finalOverrides, setFinalOverrides] = useState<Record<string, number>>({});

  // Grid edits are not native inputs, so the app-wide tracker cannot see them
  // (standing rule 15).
  useUnsavedGuard(dirty);

  // ── Load ─────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (id === null) return;
    setLoading(true);
    try {
      const bundle = await fetchQuote(id);
      setQuote(bundle.header);
      setRows(bundle.rows);
      setItems(bundle.items);
      setFinalOverrides({});
      priceOverrides.current = {};
      setDirty(false);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the quote.');
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { void load(); }, [load]);

  // The prospect list keeps the quote's own prospect, so it is refetched once
  // the quote is known.
  useEffect(() => {
    void (async () => {
      try {
        setLookups(await fetchQuoteLookups(quote.prospectClientId, quote.clientId));
      } catch (e) {
        notify.error(e instanceof Error ? e.message : 'Failed to load the dropdown lists.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.oid]);

  // A new quote opens as legacy opens one (CRMQuotes.cs :51-53): Quoted Date =
  // today, Quoted By and Owner = the signed-in user. Set without marking the
  // form changed — these are defaults, not edits.
  useEffect(() => {
    if (!isNew || !lookups) return;
    setQuote((cur) => ({
      ...cur,
      quotedDate: cur.quotedDate ?? `${today()}T00:00:00`,
      quotedById: cur.quotedById ?? lookups.currentEmployeeId,
      ownerId: cur.ownerId ?? lookups.currentEmployeeId,
    }));
  }, [isNew, lookups]);

  // Projects follow the client; a prospect quote has none (CRMQuotes.cs :240).
  useEffect(() => {
    if (quote.isProspect || !quote.clientId) { setProjects([]); return; }
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchProjects(quote.clientId!);
        if (!cancelled) setProjects(list);
      } catch (e) {
        if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the projects.');
      }
    })();
    return () => { cancelled = true; };
  }, [quote.isProspect, quote.clientId, notify]);

  // Contacts follow the client — or, for a prospect, its one primary contact.
  useEffect(() => {
    const clientId   = quote.isProspect ? null : quote.clientId;
    const prospectId = quote.isProspect ? quote.prospectClientId : null;
    if (!clientId && !prospectId) { setContacts([]); return; }
    let cancelled = false;
    void (async () => {
      try {
        const list = await fetchContacts(clientId, prospectId);
        if (!cancelled) setContacts(list);
      } catch (e) {
        if (!cancelled) notify.error(e instanceof Error ? e.message : 'Failed to load the contacts.');
      }
    })();
    return () => { cancelled = true; };
  }, [quote.isProspect, quote.clientId, quote.prospectClientId, notify]);

  // ── Header edits ─────────────────────────────────────────────────────────
  function patch(p: Partial<QuoteDetail>) {
    setQuote((cur) => ({ ...cur, ...p }));
    setDirty(true);
  }

  /** What changing the client or the prospect clears (legacy :3705-3717): the
   *  project, the contact and everything that was filled in from them. */
  const PARTY_RESET: Partial<QuoteDetail> = {
    projectId: null, projectCode: null, projectName: null,
    primaryContactId: null, primaryContactName: null, emailID: null,
    cellPhone: null, officePhone: null,
    billStreet1: null, billStreet2: null, billCity: null,
    billState: null, billZipCode: null, billCountry: null,
  };

  /** The Bill To address a client or prospect brings with it. Legacy shows it
   *  through the BO getters whenever the stored field is blank
   *  (CRMQuotes.cs :683-860); here it is filled in on the pick and stays
   *  editable. */
  function billFrom(party: PartyOption | undefined): Partial<QuoteDetail> {
    if (!party) return {};
    return {
      billStreet1: party.street1, billStreet2: party.street2, billCity: party.city,
      billState: party.state, billZipCode: party.zipCode, billCountry: party.country,
    };
  }

  function setProspectMode(isProspect: boolean) {
    // Legacy :3815-3829 clears both parties along with everything that hung
    // off them.
    patch({
      ...PARTY_RESET, isProspect,
      clientId: null, clientName: null, prospectClientId: null, prospectClientName: null,
    });
  }

  function setClient(clientId: string | null) {
    const party = lookups?.clients.find((c) => c.id === clientId);
    patch({ ...PARTY_RESET, clientId, clientName: party?.name ?? null, ...billFrom(party) });
  }

  function setProspect(prospectId: string | null) {
    const party = lookups?.prospects.find((c) => c.id === prospectId);
    patch({ ...PARTY_RESET, prospectClientId: prospectId, prospectClientName: party?.name ?? null, ...billFrom(party) });
  }

  function setProject(projectId: string | null) {
    const project = projects.find((p) => p.id === projectId);
    patch({ projectId, projectCode: project?.name ?? null, projectName: project?.projectName ?? null });
  }

  function setContact(contactId: string | null) {
    const contact = contacts.find((c) => c.id === contactId);
    // Legacy fills Email from the contact on every pick (CRMQuotes.cs :541-548)
    // and the phones whenever they are still blank (:575-640).
    patch({
      primaryContactId: contactId,
      primaryContactName: contact?.name ?? null,
      emailID: contact?.email ?? null,
      cellPhone: quote.cellPhone || contact?.mobilePhone || null,
      officePhone: quote.officePhone || contact?.officePhone || null,
    });
  }

  /** Legacy :3756-3775 — ticking copies Bill To into Report To; un-ticking
   *  clears Report To. While ticked, Report To simply mirrors Bill To. */
  function setSameAddress(same: boolean) {
    patch(same
      ? { sameAddress: true }
      : { sameAddress: false, shipStreet1: null, shipStreet2: null, shipCity: null,
          shipState: null, shipZipCode: null, shipCountry: null });
  }

  /** Legacy opens a "Quote Cancel Reason" popup the moment Cancel is ticked
   *  (:3778-3797), and a save with Cancel ticked and no reason is refused. */
  async function setCancel(cancel: boolean) {
    if (!cancel) { patch({ cancel: false, cancelReason: null }); return; }
    const reason = await prompt({
      title: 'Quote Cancel Reason',
      message: 'Why is this quote being cancelled?',
      required: true,
      initialValue: quote.cancelReason ?? '',
    });
    if (reason === null) return;           // the tick does not take
    patch({ cancel: true, cancelReason: reason.trim() });
  }

  // ── Pricing rows ─────────────────────────────────────────────────────────
  /** UnitPrice / TotalTierPrice the user TYPED, by row id — sent with every
   *  calculation so a later quantity edit keeps them (legacy :7808-7830). At most
   *  one per row: typing one clears the other; a TAT change clears both. */
  const priceOverrides = useRef<Record<string, { unit?: number; total?: number; keepDiscount?: boolean }>>({});

  const rowInputs = useCallback((list: QuoteAnalysisRow[]): AnalysisRowInput[] =>
    list.map((r) => ({
      oid: r.oid,
      priceCodeId: r.isNew ? r.priceCodeId : null,
      sampleMatrixId: r.sampleMatrixId,
      tatId: r.tatId,
      unitPriceOverride: priceOverrides.current[r.oid]?.unit ?? null,
      totalTierPriceOverride: priceOverrides.current[r.oid]?.total ?? null,
      unitPriceKeepsDiscount: priceOverrides.current[r.oid]?.keepDiscount ?? false,
      qty: r.qty, discount: r.discount,
      testDescription: r.testDescription, remark: r.remark, sort: r.sort,
    })), []);

  const itemInputs = useCallback((list: QuoteItemRow[], overrides: Record<string, number>): ItemRowInput[] =>
    list.map((i) => ({
      oid: i.oid,
      itemPriceId: i.isNew ? i.itemPriceId : null,
      qty: i.qty, discount: i.discount,
      finalAmountOverride: overrides[i.oid] ?? null,
      remark: i.remark,
    })), []);

  /** Requests are numbered so a slow answer can never overwrite a newer one. */
  const calcSeq = useRef(0);

  /**
   * Send the draft to the server and take back the priced rows and the totals.
   * Returns false when the server refused it (e.g. a quantity of zero), in
   * which case nothing on screen changes.
   */
  const recalculate = useCallback(async (
    nextRows: AnalysisRowInput[], nextItems: ItemRowInput[],
    global?: { percent?: number; amount?: number },
  ): Promise<boolean> => {
    const seq = ++calcSeq.current;
    try {
      const priced = await calculateQuote({
        quoteId: id, rows: nextRows, items: nextItems,
        ...(global?.percent !== undefined ? { globalDiscount: global.percent } : {}),
        ...(global?.amount  !== undefined ? { globalDiscountAmount: global.amount } : {}),
      });
      if (seq !== calcSeq.current) return true;      // superseded
      setRows(priced.rows);
      setItems(priced.items);
      setQuote((cur) => ({
        ...cur,
        detailedAmount: priced.totals.detailedAmount,
        discountAmount: priced.totals.discountAmount,
        discount:       priced.totals.discount,
        totalAmount:    priced.totals.totalAmount,
        quotedAmount:   priced.totals.quotedAmount,
      }));
      setDirty(true);
      return true;
    } catch (e) {
      const message = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.warning(message ?? (e instanceof Error ? e.message : 'Could not price the quote.'));
      return false;
    }
  }, [id, notify]);

  function addPriceCodes(picked: PriceCodeCandidate[]) {
    setPickCodes(false);
    if (picked.length === 0) return;
    const added: AnalysisRowInput[] = picked.map((c) => ({
      // The id chosen here becomes the row's id on the server, so the row has
      // one identity from this moment through to the save.
      oid: crypto.randomUUID(),
      priceCodeId: c.priceCodeId, sampleMatrixId: c.sampleMatrixId,
      qty: 1, discount: 0, sort: 0,
    }));
    void recalculate([...rowInputs(rows), ...added], itemInputs(items, finalOverrides));
  }

  function addItemCharges(picked: ItemChargeCandidate[]) {
    setPickItems(false);
    if (picked.length === 0) return;
    const added: ItemRowInput[] = picked.map((c) => ({
      oid: crypto.randomUUID(), itemPriceId: c.itemPriceId, qty: 1, discount: 0,
    }));
    void recalculate(rowInputs(rows), [...itemInputs(items, finalOverrides), ...added]);
  }

  function removeRows() {
    if (rowSel.size === 0) return;
    const kept = rows.filter((r) => !rowSel.has(r.oid));
    setRowSel(new Set());
    void recalculate(rowInputs(kept), itemInputs(items, finalOverrides));
  }

  function removeItems() {
    if (itemSel.size === 0) return;
    const kept = items.filter((i) => !itemSel.has(i.oid));
    const overrides = Object.fromEntries(
      Object.entries(finalOverrides).filter(([oid]) => kept.some((i) => i.oid === oid)));
    setItemSel(new Set());
    setFinalOverrides(overrides);
    void recalculate(rowInputs(rows), itemInputs(kept, overrides));
  }

  /** A whole number from a grid cell, or null when it is not one. */
  function wholeNumber(text: string): number | null {
    const n = Number(text);
    return text.trim() !== '' && Number.isInteger(n) ? n : null;
  }
  function decimalNumber(text: string): number | null {
    const n = Number(text);
    return text.trim() !== '' && Number.isFinite(n) ? n : null;
  }

  function commitRowCell(row: QuoteAnalysisRow, column: AppColumn<QuoteAnalysisRow>, text: string) {
    let next: QuoteAnalysisRow;
    switch (column.field) {
      case 'qty': {
        const qty = wholeNumber(text);
        // Legacy wording, verbatim (QuoteViewController :7464).
        if (qty === null || qty <= 0) { notify.warning('Qty value must be greater than 0.'); return; }
        next = { ...row, qty };
        break;
      }
      case 'discount': {
        const discount = decimalNumber(text);
        if (discount === null) { notify.warning('Discount must be a number.'); return; }
        // Mirrors the server rule (QuoteRules.DiscountTooHigh): refused here so the
        // grid never shows a negative price that the save would then reject.
        if (discount > 100) { notify.warning('Discount cannot be more than 100%.'); return; }
        // Legacy :7831-7834 — a discount edit puts UnitPrice back to the price-list
        // unit, so a typed UnitPrice no longer applies.
        if (priceOverrides.current[row.oid]?.unit !== undefined) delete priceOverrides.current[row.oid];
        next = { ...row, discount };
        break;
      }
      case 'sort': {
        const sort = wholeNumber(text);
        if (sort === null) { notify.warning('Sort must be a whole number.'); return; }
        next = { ...row, sort };
        break;
      }
      case 'unitPrice':
      case 'totalTierPrice': {
        const value = decimalNumber(text);
        if (value === null) { notify.warning('Enter a number.'); return; }
        // Legacy LDMMessages "NotallowednegativeValue", verbatim.
        if (value < 0) { notify.warning('Not allowed negative value.'); return; }
        priceOverrides.current[row.oid] = column.field === 'unitPrice' ? { unit: value } : { total: value };
        next = row;
        break;
      }
      case 'testDescription': next = { ...row, testDescription: text.trim() || null }; break;
      case 'remark':          next = { ...row, remark: text.trim() || null };          break;
      default: return;
    }
    const list = rows.map((r) => (r.oid === row.oid ? next : r));
    // Text-only edits change no number, so they need no round trip.
    if (column.field === 'testDescription' || column.field === 'remark' || column.field === 'sort') {
      setRows(list);
      setDirty(true);
      return;
    }
    void recalculate(rowInputs(list), itemInputs(items, finalOverrides));
  }

  // ── TAT and Sample Matrix cell pickers (legacy cell-click popups) ─────────
  const [tatRow, setTatRow] = useState<QuoteAnalysisRow | null>(null);
  const [matrixRow, setMatrixRow] = useState<QuoteAnalysisRow | null>(null);

  /** A new TAT re-prices the row from the price list (server side); typed
   *  prices no longer apply to the new price. */
  function pickTat(oids: string[], tatId: string) {
    setTatRow(null);
    for (const oid of oids) delete priceOverrides.current[oid];
    const list = rows.map((r) => (oids.includes(r.oid) ? { ...r, tatId } : r));
    void recalculate(rowInputs(list), itemInputs(items, finalOverrides));
  }

  function pickSampleMatrix(oid: string, sampleMatrixId: string) {
    setMatrixRow(null);
    const list = rows.map((r) => (r.oid === oid ? { ...r, sampleMatrixId } : r));
    void recalculate(rowInputs(list), itemInputs(items, finalOverrides));
  }

  // ── Copy To All Cell (legacy right-click on the pricing grids) ───────────
  // The shared CellContextMenu: copy the clicked cell to the ticked rows, or to
  // every row. Analysis: Qty, Discount, TAT, TestDescription; Item Charge: Qty,
  // Discount, FinalAmount (legacy C:5274-5370, 5092-5182).
  const [copyMenu, setCopyMenu] = useState<{
    grid: 'rows' | 'items'; field: string; label: string; value: unknown; x: number; y: number;
  } | null>(null);

  function openCopyMenu(grid: 'rows' | 'items', field: string, label: string, value: unknown,
                        e: { preventDefault: () => void; clientX: number; clientY: number }) {
    e.preventDefault();
    if (lockedRef.current) return;          // a validated quote is view-only
    if (!canBulkCopy(value)) { notify.warning('Cell is empty.'); return; }
    setCopyMenu({ grid, field, label, value, x: e.clientX, y: e.clientY });
  }

  function applyCopy(toSelected: boolean) {
    if (!copyMenu) return;
    const { grid, field, value } = copyMenu;
    setCopyMenu(null);
    if (grid === 'rows') {
      const targets = toSelected ? rows.filter((r) => rowSel.has(r.oid)).map((r) => r.oid) : rows.map((r) => r.oid);
      if (toSelected && targets.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
      if (field === 'tatId') { pickTat(targets, String(value)); return; }
      if (field === 'discount') for (const oid of targets) delete priceOverrides.current[oid];
      const list = rows.map((r) => (targets.includes(r.oid) ? { ...r, [field]: value } : r));
      if (field === 'testDescription') { setRows(list); setDirty(true); return; }
      void recalculate(rowInputs(list), itemInputs(items, finalOverrides));
      return;
    }
    const targets = toSelected ? items.filter((i) => itemSel.has(i.oid)).map((i) => i.oid) : items.map((i) => i.oid);
    if (toSelected && targets.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    let overrides = finalOverrides;
    let list = items;
    if (field === 'finalAmount') {
      overrides = { ...finalOverrides, ...Object.fromEntries(targets.map((oid) => [oid, Number(value)])) };
    } else {
      // A copied Qty / Discount makes a typed final amount meaningless.
      overrides = Object.fromEntries(Object.entries(finalOverrides).filter(([oid]) => !targets.includes(oid)));
      list = items.map((i) => (targets.includes(i.oid) ? { ...i, [field]: value } : i));
    }
    setFinalOverrides(overrides);
    void recalculate(rowInputs(rows), itemInputs(list, overrides));
  }

  function commitItemCell(item: QuoteItemRow, column: AppColumn<QuoteItemRow>, text: string) {
    let next = item;
    let overrides = finalOverrides;
    switch (column.field) {
      case 'qty': {
        const qty = wholeNumber(text);
        if (qty === null || qty <= 0) { notify.warning('Qty value must be greater than 0.'); return; }
        next = { ...item, qty };
        // A new quantity makes a previously typed final amount meaningless.
        overrides = Object.fromEntries(Object.entries(finalOverrides).filter(([oid]) => oid !== item.oid));
        break;
      }
      case 'discount': {
        const discount = decimalNumber(text);
        if (discount === null) { notify.warning('Discount must be a number.'); return; }
        // Mirrors the server rule (QuoteRules.DiscountTooHigh): refused here so the
        // grid never shows a negative price that the save would then reject.
        if (discount > 100) { notify.warning('Discount cannot be more than 100%.'); return; }
        next = { ...item, discount };
        overrides = Object.fromEntries(Object.entries(finalOverrides).filter(([oid]) => oid !== item.oid));
        break;
      }
      case 'finalAmount': {
        // Legacy lets Final Amount be typed and works the discount back from it
        // (:8633-8648).
        const final = decimalNumber(text.replace(/[$,]/g, ''));
        if (final === null) { notify.warning('Final Amount must be a number.'); return; }
        overrides = { ...finalOverrides, [item.oid]: final };
        break;
      }
      case 'remark': {
        setItems(items.map((i) => (i.oid === item.oid ? { ...item, remark: text.trim() || null } : i)));
        setDirty(true);
        return;
      }
      default: return;
    }
    const list = items.map((i) => (i.oid === item.oid ? next : i));
    setFinalOverrides(overrides);
    void recalculate(rowInputs(rows), itemInputs(list, overrides));
  }

  /** The header's Discount % / Discount Amount are GLOBAL — typing either sets
   *  that discount on every row of both grids (legacy :3887-3975). */
  function applyGlobal(kind: 'percent' | 'amount', text: string) {
    const value = decimalNumber(text);
    if (value === null) return;
    if (rows.length === 0 && items.length === 0) {
      // Legacy wording, verbatim (LDMMessages "AddAnalysisPriceOrItemCharge").
      notify.info('Add the analysis price/ item charge  and continue.');
      return;
    }
    if (kind === 'percent' && value === quote.discount) return;
    if (kind === 'amount'  && value === quote.discountAmount) return;
    // A global discount replaces any typed final amounts.
    setFinalOverrides({});
    // A typed UnitPrice works the row's discount BACK from the price list, so
    // on its own it would overwrite the global percentage on the next
    // calculation or the save (found in the browser walkthrough 2026-10-20: a
    // 10% global discount came back as the row's own 17% once saved). Legacy
    // keeps the typed price and applies the global % to it, so the override
    // stays and is marked to take the row's discount instead.
    for (const o of Object.values(priceOverrides.current)) {
      if (o.unit !== undefined) o.keepDiscount = true;
    }
    void recalculate(rowInputs(rows), itemInputs(items, {}),
                     kind === 'percent' ? { percent: value } : { amount: value });
  }

  // ── Save / delete ────────────────────────────────────────────────────────
  function toInput(): QuoteInput {
    return {
      lockVersion: isNew ? null : quote.lockVersion,
      title: quote.title, isProspect: quote.isProspect,
      clientId: quote.clientId, prospectClientId: quote.prospectClientId,
      tatId: quote.tatId, paymentMethodId: quote.paymentMethodId,
      expirationDate: quote.expirationDate,
      cancel: quote.cancel, cancelReason: quote.cancelReason,
      projectId: quote.projectId, quotedDate: quote.quotedDate, quotedById: quote.quotedById,
      remark: quote.remark,
      primaryContactId: quote.primaryContactId, emailID: quote.emailID,
      cellPhone: quote.cellPhone, officePhone: quote.officePhone,
      billStreet1: quote.billStreet1, billStreet2: quote.billStreet2, billCity: quote.billCity,
      billState: quote.billState, billZipCode: quote.billZipCode, billCountry: quote.billCountry,
      sameAddress: quote.sameAddress,
      shipStreet1: quote.shipStreet1, shipStreet2: quote.shipStreet2, shipCity: quote.shipCity,
      shipState: quote.shipState, shipZipCode: quote.shipZipCode, shipCountry: quote.shipCountry,
      ownerId: quote.ownerId, creditId: quote.creditId, sourceOpportunityId: quote.sourceOpportunityId,
      rows: rowInputs(rows),
      items: itemInputs(items, finalOverrides),
    };
  }

  /**
   * The Save trio. The rules are the server's (§14b.5); the three checked here
   * are only the ones a user hits most, shown before the confirm dialog rather
   * than after it, in legacy's order and legacy's words.
   */
  async function save(after: 'stay' | 'new' | 'close') {
    if (locked) return;
    if (!quote.title?.trim()) { notify.error('Title should not empty.'); return; }
    if (quote.isProspect ? !quote.prospectClientId : !quote.clientId) {
      notify.error('Client should not empty.'); return;
    }
    if (rows.length === 0) { notify.error('Please add price codes in Analysis pricing.'); return; }

    let savedId: string | null = null;
    await runSave({
      formName: FORM_NAME,
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        const res = isNew ? await createQuote(toInput()) : await updateQuote(id!, toInput());
        savedId = res.oid;
      },
      onSaved: () => {
        setDirty(false);
        onChanged();
        if (after === 'close') onClose();
        else if (after === 'new') onNew();
        else if (isNew && savedId) onCreated(savedId);
        else void load();
      },
    });
  }

  /**
   * Preview — the quote as a PDF. Legacy commits the form first
   * (`ObjectSpace.CommitChanges()`, :394) so the report shows what is on
   * screen; here a quote with unsaved edits is simply asked to save first,
   * rather than saved behind the user's back.
   */
  // ── Validate (Won / Lost) ────────────────────────────────────────────────
  // Legacy QuoteValidate: on a saved quote that is Pending Validation. Legacy
  // commits any pending edits with the validation; here the user saves first, so
  // the outcome is recorded against what is actually stored.
  // ── Lookup New / Edit buttons (legacy XAF lookup editor buttons) ─────────
  // Each opens the master's own popup; on save the dropdown lists are re-read
  // and the new or edited row is selected.
  const [newClient, setNewClient]     = useState(false);
  const [newContact, setNewContact]   = useState(false);
  const [namedEdit, setNamedEdit] = useState<{ kind: NamedLookupKind; id: string | null; name: string | null } | null>(null);

  async function reloadLookups(clientId: string | null = quote.clientId): Promise<QuoteLookups | null> {
    try {
      const fresh = await fetchQuoteLookups(quote.prospectClientId, clientId);
      setLookups(fresh);
      return fresh;
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the dropdown lists.');
      return null;
    }
  }

  const NAMED_FIELD: Record<NamedLookupKind, keyof QuoteDetail> = {
    'payment-method': 'paymentMethodId', credit: 'creditId', 'source-opportunity': 'sourceOpportunityId',
  };

  /** Legacy's New on a lookup always opens a BLANK create form, even when a
   *  value is selected (XAF LookupPropertyEditor). */
  function openNamed(kind: NamedLookupKind) {
    setNamedEdit({ kind, id: null, name: null });
  }

  const [validating, setValidating] = useState(false);
  const [validateBusy, setValidateBusy] = useState(false);
  const canValidate = perm.write && !isNew && !loading && !quote.cancel
    && (quote.status ?? '').toLowerCase() === 'pending validation';

  function openValidate() {
    if (!canValidate) return;
    if (dirty) { notify.warning('Save the quote before validating it.'); return; }
    if (quote.isProspect ? !quote.prospectClientId : !quote.clientId) {
      notify.warning('Client must not be empty.'); return;
    }
    if (rows.length === 0) { notify.warning('Please add pricecode.'); return; }
    setValidating(true);
  }

  async function acceptValidate(won: boolean, lostReason: string | null) {
    if (!won && !lostReason) { notify.info('Please enter the reason.'); return; }
    setValidateBusy(true);
    try {
      const { message } = await validateQuote(id!, { won, lostReason, lockVersion: quote.lockVersion });
      setValidating(false);
      notify.success(message);
      onChanged();
      await load();
    } catch (e) {
      // A refusal (e.g. a client of that name already exists) keeps the popup
      // open, as legacy does.
      notify.error(errorMessage(e) ?? 'Failed to validate the quote.');
    } finally {
      setValidateBusy(false);
    }
  }

  /** Refresh — reload the stored quote (legacy XAF Refresh), asking first when
   *  there are unsaved changes. */
  function refresh() {
    if (isNew) return;
    void guardedExit(() => { void load(); });
  }

  async function preview() {
    if (isNew || dirty) {
      notify.warning('Save the quote before previewing it.');
      return;
    }
    try {
      const filename = await downloadQuoteReport(id!);
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    }
  }

  const navPos  = id ? siblingIds.indexOf(id) : -1;
  const navPrev = navPos > 0 ? siblingIds[navPos - 1]! : null;
  const navNext = navPos >= 0 && navPos < siblingIds.length - 1 ? siblingIds[navPos + 1]! : null;

  // Keyboard: the Save trio + Cancel, declared next to the buttons they mirror
  // (standing rule 14, tier b).
  useHotkeys([
    { combo: 'mod+s',       label: 'Save',         group: 'Open Quote', run: () => void save('stay'),  disabled: loading || locked, order: 1 },
    { combo: 'mod+shift+s', label: 'Save & New',   group: 'Open Quote', run: () => void save('new'),   disabled: loading || locked || !perm.create, order: 2 },
    { combo: 'mod+enter',   label: 'Save & Close', group: 'Open Quote', run: () => void save('close'), disabled: loading || locked, order: 3 },
    // A key press does not pass through the click that the unsaved-changes
    // tracker watches, so these three ask for themselves (standing rule 15).
    { combo: 'esc',         label: 'Cancel',       group: 'Open Quote', run: () => void guardedExit(() => onClose()), order: 4 },
    { combo: 'alt+p',       label: 'Preview',      group: 'Open Quote', order: 4.5,
      disabled: isNew || loading, run: () => void preview() },
    { combo: 'alt+n',       label: 'New',          group: 'Open Quote', order: 4.6,
      disabled: !perm.create, run: () => void guardedExit(() => onNew()) },
    { combo: 'alt+v',       label: 'Validate',     group: 'Open Quote', order: 4.7,
      disabled: !canValidate, run: () => openValidate() },
    { combo: 'alt+r',       label: 'Refresh',      group: 'Open Quote', order: 4.8,
      disabled: isNew || loading, run: () => refresh() },
    { combo: 'alt+left',    label: 'Previous record', group: 'Open Quote', order: 5,
      disabled: !onNavigate || !navPrev, run: () => void guardedExit(() => { if (navPrev) onNavigate?.(navPrev); }) },
    { combo: 'alt+right',   label: 'Next record',     group: 'Open Quote', order: 6,
      disabled: !onNavigate || !navNext, run: () => void guardedExit(() => { if (navNext) onNavigate?.(navNext); }) },
  ]);

  // ── Grid columns ─────────────────────────────────────────────────────────
  // Legacy CRMQuotes_AnalysisPricing_ListView (Model.xafml :11435). Editable,
  // exactly as there: TestDescription · Qty · Discount(%) · Remark · Sort.
  const rowColumns: AppColumn<QuoteAnalysisRow>[] = useMemo(() => [
    { field: 'testDescription', header: 'TestDescription', width: 170, edit: { type: 'text' },
      onCellContextMenu: (e, r) => openCopyMenu('rows', 'testDescription', 'TestDescription', r.testDescription, e) },
    { field: 'matrix',       header: 'Matrix',       width: 100 },
    { field: 'test',         header: 'Test',         width: 170 },
    { field: 'isGroup',      header: 'IsGroup',      width: 80, align: 'center',
      render: (r) => <input type="checkbox" checked={r.isGroup} readOnly disabled aria-label="IsGroup" /> },
    { field: 'method',       header: 'Method',       width: 120 },
    // Click to move the row to another sample matrix of its Matrix (legacy :9038).
    { field: 'sampleMatrix', header: 'SampleMatrix', width: 130,
      render: (r) => <button type="button" className="qt-cellpick" title="Change the sample matrix"
                             disabled={lockedRef.current} onClick={() => setMatrixRow(r)}>{r.sampleMatrix ?? '—'}</button> },
    { field: 'component',    header: 'Component',    width: 110 },
    { field: 'parameter',    header: 'Parameter',    width: 100 },
    { field: 'chargeTypeCode', header: 'ChargeType', width: 100, render: (r) => CHARGE_TYPE[r.chargeTypeCode] ?? '' },
    // Click to re-price the row at another turnaround (legacy :9038-9415, :9483).
    { field: 'tat',          header: 'TAT',          width: 100,
      render: (r) => <button type="button" className="qt-cellpick" title="Change the TAT"
                             disabled={lockedRef.current} onClick={() => setTatRow(r)}>{r.tat ?? '—'}</button>,
      onCellContextMenu: (e, r) => openCopyMenu('rows', 'tatId', 'TAT', r.tatId, e) },
    { field: 'priority',     header: 'Priority',     width: 90 },
    { field: 'priceCode',    header: 'PriceCode',    width: 110 },
    { field: 'qty',          header: 'Qty',          width: 70, align: 'right', type: 'number', edit: { type: 'number' },
      onCellContextMenu: (e, r) => openCopyMenu('rows', 'qty', 'Qty', r.qty, e),
      summary: (list) => `Sum=${list.reduce((n, r) => n + r.qty, 0)}` },
    { field: 'unitPrice',    header: 'UnitPrice',    width: 100, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice),
      edit: { type: 'number', display: (v) => money.format(Number(v) || 0) },
      summary: (list) => `Sum=${money.format(list.reduce((n, r) => n + r.unitPrice, 0))}` },
    // Tier (constituent) pricing — legacy shows the band the row was priced at.
    { field: 'tierNo',       header: 'TierNo',       width: 70, align: 'right', type: 'number' },
    { field: 'tierFrom',     header: 'From',         width: 70, align: 'right', type: 'number' },
    { field: 'tierTo',       header: 'To',           width: 70, align: 'right', type: 'number' },
    { field: 'totalTierPrice', header: 'TotalTierPrice', width: 120, align: 'right', type: 'number', render: (r) => money.format(r.totalTierPrice),
      edit: { type: 'number', display: (v) => money.format(Number(v) || 0) },
      summary: (list) => `Sum=${money.format(list.reduce((n, r) => n + r.totalTierPrice, 0))}` },
    { field: 'discount',     header: 'Discount(%)',  width: 110, align: 'right', type: 'number', edit: { type: 'number', display: (v) => (Number(v) || 0).toFixed(2) }, render: (r) => r.discount.toFixed(2),
      onCellContextMenu: (e, r) => openCopyMenu('rows', 'discount', 'Discount(%)', r.discount, e) },
    { field: 'finalAmount',  header: 'FinalAmount',  width: 120, align: 'right', type: 'number', render: (r) => money.format(r.finalAmount),
      summary: (list) => `Sum=${money.format(list.reduce((n, r) => n + r.finalAmount, 0))}` },
    { field: 'remark',       header: 'Remark',       width: 160, edit: { type: 'text' } },
    { field: 'sort',         header: 'Sort',         width: 70, align: 'right', type: 'number', edit: { type: 'number' } },
  ], []);

  // Legacy CRMQuotes_QuotesItemChargePrice_ListView. Editable: Qty ·
  // Discount(%) · FinalAmount.
  const itemColumns: AppColumn<QuoteItemRow>[] = useMemo(() => [
    { field: 'itemCode',  header: 'ItemCode',  width: 120 },
    { field: 'itemName',  header: 'ItemName',  width: 220 },
    { field: 'category',  header: 'Category',  width: 140 },
    { field: 'unit',      header: 'Unit',      width: 90 },
    { field: 'unitPrice', header: 'UnitPrice', width: 110, align: 'right', type: 'number', render: (r) => money.format(r.unitPrice) },
    { field: 'qty',       header: 'Qty',       width: 70, align: 'right', type: 'number', edit: { type: 'number' },
      onCellContextMenu: (e, i) => openCopyMenu('items', 'qty', 'Qty', i.qty, e),
      summary: (list) => `Sum=${list.reduce((n, i) => n + i.qty, 0)}` },
    { field: 'amount',    header: 'Amount',    width: 110, align: 'right', type: 'number', render: (r) => money.format(r.amount),
      summary: (list) => `Sum=${money.format(list.reduce((n, i) => n + i.amount, 0))}` },
    { field: 'discount',  header: 'Discount(%)', width: 110, align: 'right', type: 'number', edit: { type: 'number', display: (v) => (Number(v) || 0).toFixed(2) }, render: (r) => r.discount.toFixed(2),
      onCellContextMenu: (e, i) => openCopyMenu('items', 'discount', 'Discount(%)', i.discount, e) },
    { field: 'finalAmount', header: 'FinalAmount', width: 120, align: 'right', type: 'number', edit: { type: 'number', display: (v) => money.format(Number(v) || 0) }, render: (r) => money.format(r.finalAmount),
      onCellContextMenu: (e, i) => openCopyMenu('items', 'finalAmount', 'FinalAmount', i.finalAmount, e),
      summary: (list) => `Sum=${money.format(list.reduce((n, i) => n + i.finalAmount, 0))}` },
    { field: 'remark',    header: 'Remark',    width: 160, edit: { type: 'text' } },
  ], []);

  // ── Field helpers ────────────────────────────────────────────────────────
  // The SHARED detail-view markup (`.sd-row` > `.sd-label` + `.sd-input`), so
  // label width, the 26px control, the 6px row gap and the yellow / grey cue
  // all come from SettingsDetailPage.css — the same rules the reference form
  // (Settings ▸ Inventory Management ▸ Items) renders with (standing rule 12).
  function field(label: string, control: ReactNode, required = false) {
    return (
      <div className="sd-row" key={label}>
        <label className="sd-label">
          {label}{required && <span className="req">*</span>}
        </label>
        {/* The label is a sibling, not a wrapper, so the control is named
            directly: that is what lets a screen reader say WHICH dropdown this is. */}
        {isValidElement<{ 'aria-label'?: string }>(control) && typeof control.type === 'string'
          ? cloneElement(control, { 'aria-label': label })
          : control}
      </div>
    );
  }

  /** A tick box, laid out as the shared page lays one out: an empty label cell
   *  keeps the box in the field column, the caption sits beside it. */
  function tick(label: string, checked: boolean, onChange: (v: boolean) => void) {
    return (
      <div className="sd-row sd-row--check" key={label}>
        <span className="sd-label" aria-hidden="true">&nbsp;</span>
        <label className="sd-check">
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
          {label}
        </label>
      </div>
    );
  }

  /**
   * One titled CARD of fields — the shared `.sd-body--cards` opt-in in
   * SettingsDetailPage.css (owner request 2026-10-20). `left` runs down the
   * card's first column and `right` down its second, each a label-beside-field
   * grid, with a rule between them. `extra` goes on the right of the card's
   * header strip (Report To's "Same as Bill To").
   */
  function section(title: string, left: ReactNode[], right?: ReactNode[], extra?: ReactNode) {
    const col = (rows: ReactNode[]) => (
      <div className="sd-card-col">
        <div className="sd-grid sd-grid--single sd-grid--side">{rows.filter(Boolean)}</div>
      </div>
    );
    return (
      <section className="sd-card" aria-label={title} key={title}>
        <div className="sd-card-h">
          <h3 className="sd-section-h">{title}</h3>
          {extra}
        </div>
        {right
          ? <div className="sd-card-cols">{col(left)}{col(right)}</div>
          : col(left)}
      </section>
    );
  }

  function text(key: keyof QuoteDetail, opts: { required?: boolean; readOnly?: boolean } = {}) {
    const value = (quote[key] as string | null) ?? '';
    if (opts.readOnly) return <input type="text" className="sd-input readonly" readOnly value={value} />;
    const tint = opts.required && !value.trim() ? ' required-empty' : '';
    return (
      // autoComplete off: the Bill To / Report To fields look like a postal
      // address to the browser, which then offers to "Save address" to the
      // user's own account — a client's address is not the user's to keep.
      <input type="text" className={`sd-input${tint}`} value={value} maxLength={100} autoComplete="off"
             onChange={(e) => patch({ [key]: e.target.value || null } as Partial<QuoteDetail>)} />
    );
  }

  const readOnlyMoney = (value: number) =>
    <input type="text" className="sd-input readonly qt-money" readOnly value={money.format(value)} />;

  /** A button beside a GRID lookup, as legacy's XAF LookupPropertyEditor draws
   *  them: New (new-document glyph — always a BLANK create form) or the eraser
   *  (clears the value to N/A). There is no Edit button in legacy; the blue
   *  glyph on its lookups is the eraser. `field` names the lookup for the
   *  tooltip and the screen reader. */
  function lookupBtn(kind: 'new' | 'clear', field: string, disabled: boolean, onClick: () => void) {
    const label = kind === 'new' ? `New ${field}` : `Clear ${field}`;
    return (
      <button key={kind} type="button" className={`qt-lkbtn qt-lkbtn--${kind}`} data-field={field}
              title={label} aria-label={label} disabled={disabled} onClick={onClick}>
        <i className={`ti ${kind === 'new' ? 'ti-file-plus' : 'ti-eraser'}`} aria-hidden="true" />
      </button>
    );
  }

  /** An FK lookup as legacy draws it (XAF LookupPropertyEditor): the shared
   *  AiCombo dropdown, then — as SEPARATE square buttons, the look the owner
   *  chose for every lookup on this form (2026-10-20) — New where legacy has one
   *  and the eraser that clears the value. `newTitle` names the field. */
  function fk(
    value: string | null, options: Array<{ id: string; name: string | null }>,
    onPick: (id: string | null) => void, onNew?: () => void, newTitle?: string,
  ) {
    const field = (newTitle ?? '').replace(/^New /, '');
    const combo = (
      <AiCombo value={value} options={options.map((o) => ({ value: o.id, label: o.name ?? '' }))}
               onChange={onPick} ariaLabel={field} />
    );
    return withButtons(combo, [
      ...(onNew ? [lookupBtn('new', field, false, onNew)] : []),
      lookupBtn('clear', field, false, () => onPick(null)),
    ]);
  }

  /** A control with its lookup buttons on its right, as legacy lays them out.
   *  The control keeps its accessible name (the caption is a sibling cell). */
  function withButtons(control: ReactNode, buttons: ReactNode[]) {
    const first = buttons[0];
    const name = isValidElement<{ 'data-field'?: string }>(first) ? first.props['data-field'] : undefined;
    const named = name && isValidElement<{ 'aria-label'?: string }>(control) && typeof control.type === 'string'
      ? cloneElement(control, { 'aria-label': name })
      : control;
    return <div className="qt-lkwrap">{named}{buttons}</div>;
  }

  /** The bar over each pricing grid — legacy Add · Save · Remove · Export to.
   *  Save commits the whole quote, as legacy's grid Save does. */
  function childBar(
    onAdd: () => void, onRemove: () => void, removeDisabled: boolean,
    filename: string, matrix: ReturnType<typeof gridExportMatrix>,
  ) {
    return (
      <div className="qt-childbar">
        {mayEdit && (
          <>
            <button type="button" className="ca-btn" onClick={onAdd} disabled={locked}>
              <i className="ti ti-plus ai-circ ai-circ-add" aria-hidden="true" /> Add
            </button>
            <button type="button" className="ca-btn" onClick={() => void save('stay')} disabled={loading || locked}>
              <span className="tb-i" aria-hidden="true">💾</span> Save
            </button>
            <button type="button" className="ca-btn" onClick={onRemove} disabled={removeDisabled || locked}>
              <i className="ti ti-minus ai-circ ai-circ-del" aria-hidden="true" /> Remove
            </button>
          </>
        )}
        <ExportMenu filename={filename} {...matrix} triggerClassName="ca-btn" />
      </div>
    );
  }

  // ── Action bar, identical top and bottom ─────────────────────────────────
  // The shared `.sd-actionbar` / `.sd-abtn`, in the reference form's order and
  // wording: Save · Save and Close · Save and New · | · Cancel ("and", never
  // "&"; a thin divider before Cancel). Preview is this form's own action, so
  // it follows the save set. There is NO Delete here — the standard keeps
  // Delete out of an edit view; a quote is deleted from the list.
  const tip = (label: string, combo: string) => `${label} (${formatCombo(combo)})`;
  const actionBar = (bottom: boolean) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      {mayEdit && (
        <>
          <button type="button" className="sd-abtn primary" title={tip('Save', 'mod+s')}
                  onClick={() => void save('stay')} disabled={loading || locked}>
            <span className="tb-i">💾</span> Save
          </button>
          <button type="button" className="sd-abtn" title={tip('Save and Close', 'mod+enter')}
                  onClick={() => void save('close')} disabled={loading || locked}>
            <span className="tb-i">💾</span> Save and Close
          </button>
          {perm.create && (
            <button type="button" className="sd-abtn" title={tip('Save and New', 'mod+shift+s')}
                    onClick={() => void save('new')} disabled={loading || locked}>
              <span className="tb-i">📄</span> Save and New
            </button>
          )}
        </>
      )}
      {/* Legacy's own detail actions, after the save set: New · Validate ·
          Preview · Refresh (Delete stays on the list — owner decision
          2026-10-20, the standard keeps it out of an edit view). */}
      {perm.create && (
        <button type="button" className="sd-abtn" title={tip('New', 'alt+n')}
                onClick={() => void guardedExit(() => onNew())}>
          <i className="ti ti-plus tb-i-green" aria-hidden="true" /> New
        </button>
      )}
      {perm.write && !isNew && (
        <button type="button" className="sd-abtn" title={tip('Validate', 'alt+v')}
                onClick={openValidate} disabled={!canValidate}>
          <i className="ti ti-circle-check tb-i-green" aria-hidden="true" /> Validate
        </button>
      )}
      {!isNew && (
        <button type="button" className="sd-abtn" title={tip('Preview', 'alt+p')}
                onClick={() => void preview()} disabled={loading}>
          <span className="tb-i">👁</span> Preview
        </button>
      )}
      {!isNew && (
        <button type="button" className="sd-abtn" title={tip('Refresh', 'alt+r')}
                onClick={refresh} disabled={loading}>
          <i className="ti ti-refresh tb-i-green" aria-hidden="true" /> Refresh
        </button>
      )}
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" title={tip('Cancel', 'esc')} onClick={onClose}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  const existingRowKeys = useMemo(() => new Set(rows.map((r) => candidateTestKey(r))), [rows]);
  const existingItemIds = useMemo(
    () => new Set(items.map((i) => (i.itemPriceId ?? '').toLowerCase())), [items]);
  const usedMatrixIds = useMemo(
    () => [...new Set(rows.map((r) => r.sampleMatrixId).filter((v): v is string => !!v))], [rows]);

  const ship = (billKey: keyof QuoteDetail, shipKey: keyof QuoteDetail) =>
    // While "Same Address" is ticked, Report To simply mirrors Bill To.
    quote.sameAddress
      ? <input type="text" className="sd-input readonly" readOnly value={(quote[billKey] as string | null) ?? ''} />
      : text(shipKey);

  // The four tabs under the form, in legacy's order.
  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'analysis',   label: 'Analysis Pricing', count: rows.length },
    { id: 'items',      label: 'Item Charge',      count: items.length },
    { id: 'attachment', label: 'Attachment',       count: docCounts.attachments },
    { id: 'note',       label: 'Notes',            count: docCounts.notes },
  ];
  const recordName = quote.quoteID ?? (isNew ? 'New' : '');
  const statusText = quote.status ?? 'Pending Validation';

  return (
    <div className="bl-page mlv-host qt-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap" ref={pageRef}>
          {/* Header — title + breadcrumb + prev/next record arrows */}
          <div className="sd-head">
            <div>
              <h2>
                {/* {BO} – {record}; legacy DetailView Caption "Open Quote" and
                    NullText "Autogenerate" on QuoteID until the first save. */}
                Open Quote – {quote.quoteID ?? 'Autogenerate'}
                {/* Where the quote stands, beside its name — the shared status
                    pill, so it reads the same as the Status column on the list. */}
                <span className={`adg-pill ${statusVariant(statusText)} sd-head-status`}>{statusText}</span>
              </h2>
              <div className="sd-bc">
                <span>Accounting</span>
                <i className="ti ti-chevron-right" />
                <span>Quotes</span>
                <i className="ti ti-chevron-right" />
                <a onClick={onClose}>Open Quotes</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="qt-headright">
              {/* This quote's own history — the shared, config-gated chip
                  (standing rule 11). A new quote has none yet. */}
              {!isNew && <AuditTrailButton variant="chip" formName={FORM_NAME} sourceId={id!} />}
              {onNavigate && siblingIds.length > 1 && (
                <div className="sd-navarrows">
                  <button type="button" className="sd-na" aria-label="Previous record"
                          title={tip('Previous record', 'alt+left')}
                          disabled={!navPrev} onClick={() => navPrev && onNavigate(navPrev)}>‹</button>
                  <button type="button" className="sd-na next" aria-label="Next record"
                          title={tip('Next record', 'alt+right')}
                          disabled={!navNext} onClick={() => navNext && onNavigate(navNext)}>›</button>
                </div>
              )}
            </div>
          </div>

          {actionBar(false)}

          <div className="sd-body sd-body--cards">
            {statusLocked && (
              <div className="qt-locked-note" role="status">
                <i className="ti ti-lock" aria-hidden="true" /> This quote is {quote.status ?? 'validated'} and can no longer be changed.
              </div>
            )}
            {/* A validated quote is view-only: one disabled fieldset greys every
                header control at once (display:contents, so the card layout is
                untouched). */}
            <fieldset className="qt-lockset" disabled={locked}>
            {section('Summary', [
              field('Quote ID', <input type="text" className="sd-input readonly" readOnly
                                       value={quote.quoteID ?? ''} placeholder="Autogenerate" />),
              field('Title', text('title', { required: true }), true),
              // Legacy grid lookups: Customer_LookupListView_CRMQuotes (Client ·
              // Address · City · ClientCode · Phone) and, for a prospect,
              // CRMProspects_LookupListView_Quotes (Prospects · Topic).
              quote.isProspect
                ? field('Client', <QuoteGridLookup label="Client" required
                    options={lookups?.prospects ?? []} value={quote.prospectClientId}
                    cols={PROSPECT_COLS} cells={prospectCells} onPick={setProspect}
                    emptyLabel={quote.prospectClientName} popupWidth={520} />, true)
                : field('Client', withButtons(<QuoteGridLookup label="Client" required
                    options={lookups?.clients ?? []} value={quote.clientId}
                    cols={CLIENT_COLS} cells={clientCells} onPick={setClient}
                    emptyLabel={quote.clientName} colWidths={[200, 180, 100, 90, 110]} />,
                    [lookupBtn('new', 'Client', false, () => setNewClient(true))]), true),
              tick('Is Prospect', quote.isProspect, setProspectMode),
              field('TAT', fk(quote.tatId, lookups?.tats ?? [], (v) => patch({ tatId: v }), undefined, 'TAT')),
              field('Payment Method', fk(quote.paymentMethodId, lookups?.paymentMethods ?? [],
                (v) => patch({ paymentMethodId: v }), () => openNamed('payment-method'), 'New Payment Method')),
              field('Expiration Date',
                <input type="date" className={`sd-input${quote.expirationDate ? '' : ' required-empty'}`}
                       min={today()} value={dateInput(quote.expirationDate)}
                       onChange={(e) => patch({ expirationDate: e.target.value ? `${e.target.value}T00:00:00` : null })} />,
                true),
              // Legacy hides Cancel until the quote has an id (CRMQuotes.cs :346)…
              !isNew && tick('Cancel', quote.cancel, (v) => void setCancel(v)),
              // …and Date Canceled / Cancel Reason until it is cancelled (:377).
              quote.cancel && field('Date Canceled',
                <input type="text" className="sd-input readonly" readOnly
                       value={fmtLocalDate(quote.dateCanceled)} placeholder="Set when saved" />),
              quote.cancel && field('Cancel Reason',
                <input type="text" className="sd-input readonly" readOnly value={quote.cancelReason ?? ''} />),
            ], [
              field('Project ID', <QuoteGridLookup label="Project ID" disabled={quote.isProspect}
                options={projects} value={quote.projectId} cols={PROJECT_COLS} cells={projectCells}
                onPick={setProject} emptyLabel={quote.projectCode} popupWidth={560} />),
              field('Project Name', text('projectName', { readOnly: true })),
              field('Quoted Date',
                <input type="date" className="sd-input" value={dateInput(quote.quotedDate)}
                       onChange={(e) => patch({ quotedDate: e.target.value ? `${e.target.value}T00:00:00` : null })} />),
              field('Quoted By', fk(quote.quotedById, lookups?.employees ?? [], (v) => patch({ quotedById: v }), undefined, 'Quoted By')),
              field('Amount', readOnlyMoney(quote.quotedAmount)),
              field('Remark',
                <textarea rows={3} className="sd-input sd-input--tall" maxLength={100} value={quote.remark ?? ''}
                          onChange={(e) => patch({ remark: e.target.value || null })} />),
            ])}

            {section('Totals', [
              field('Detailed Amount', readOnlyMoney(quote.detailedAmount)),
              field('Discount Amount',
                <GlobalNumber key={`amt-${quote.discountAmount}`} value={quote.discountAmount} currency label="Discount Amount"
                              onCommit={(t) => applyGlobal('amount', t)} />),
            ], [
              field('Discount %',
                <GlobalNumber key={`pct-${quote.discount}`} value={quote.discount} label="Discount %"
                              onCommit={(t) => applyGlobal('percent', t)} />),
              field('Amount', readOnlyMoney(quote.totalAmount)),
            ])}

            {section('Contacts', [
              field('Primary Contact', withButtons(<QuoteGridLookup label="Primary Contact"
                options={contacts} value={quote.primaryContactId} cols={CONTACT_COLS} cells={contactCells}
                onPick={setContact} emptyLabel={quote.primaryContactName} />,
                // Legacy Contact_LookupListView_Prospect AllowNew="True": New opens
                // the full contact form for this client (a prospect quote has no
                // client to file it under, so New waits for one).
                [lookupBtn('new', 'Primary Contact', quote.isProspect || !quote.clientId, () => setNewContact(true)),
                 lookupBtn('clear', 'Primary Contact', false, () => setContact(null))])),
              field('Email ID', text('emailID', { required: !!quote.primaryContactId }), !!quote.primaryContactId),
            ], [
              field('Cell Phone', text('cellPhone')),
              field('Office Phone', text('officePhone')),
            ])}

            <div className="sd-card-pair">
              {section('Bill To Address', [
                field('Street 1', text('billStreet1')),
                field('Street 2', text('billStreet2')),
                field('City', text('billCity')),
                field('State', text('billState')),
                field('Zip Code', text('billZipCode')),
                field('Country', text('billCountry')),
              ])}
              {section('Report to Address', [
                field('Street 1', ship('billStreet1', 'shipStreet1')),
                field('Street 2', ship('billStreet2', 'shipStreet2')),
                field('City', ship('billCity', 'shipCity')),
                field('State', ship('billState', 'shipState')),
                field('Zip Code', ship('billZipCode', 'shipZipCode')),
                field('Country', ship('billCountry', 'shipCountry')),
              ], undefined,
                <label className="sd-check">
                  <input type="checkbox" checked={quote.sameAddress}
                         onChange={(e) => setSameAddress(e.target.checked)} />
                  Same Address
                </label>)}
            </div>

            {section('Account Management', [
              field('Owner', fk(quote.ownerId, lookups?.employees ?? [], (v) => patch({ ownerId: v }), undefined, 'Owner')),
              field('Credit', fk(quote.creditId, lookups?.credits ?? [], (v) => patch({ creditId: v }), undefined, 'Credit')),
            ], [
              field('Source Opportunity', fk(quote.sourceOpportunityId, lookups?.opportunities ?? [],
                (v) => patch({ sourceOpportunityId: v }), () => openNamed('source-opportunity'), 'New Source Opportunity')),
              field('Status', <input type="text" className="sd-input readonly" readOnly
                                     value={statusText} />),
            ])}

            </fieldset>

            {/* The child tabs — the shared tab strip with its panel joined
                beneath it, as on the reference form. All four panels stay
                mounted and the inactive ones are hidden, so switching tabs
                costs no refetch and keeps each grid's selection. */}
            <section className="sd-card sd-card--tabs" aria-label="Pricing and documents">
              <div className="sd-assoc-tabbar sd-childtabbar" role="tablist">
                {tabs.map((t) => (
                  <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                          className={`sd-assoc-tab${tab === t.id ? ' active' : ''}`}
                          onClick={() => setTab(t.id)}>
                    {t.label}<span className="sd-assoc-tabn">{t.count}</span>
                  </button>
                ))}
              </div>

              <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" hidden={tab !== 'analysis'}>
                <AppDataGrid<QuoteAnalysisRow>
                  headerLeft={childBar(() => setPickCodes(true), removeRows, rowSel.size === 0,
                    `quote-${quote.quoteID ?? 'new'}-analysis-pricing`, gridExportMatrix(rows, rowColumns))}
                  hideHeaderExport
                  rows={rows}
                  columns={rowColumns}
                  getRowId={(r) => r.oid}
                  loading={loading}
                  selectable
                  selectedIds={rowSel}
                  onSelectionChange={setRowSel}
                  onCellCommit={locked ? undefined : commitRowCell}
                  maximizable
                  exportFilename={`quote-${quote.quoteID ?? 'new'}-analysis-pricing`}
                  emptyText="No price codes yet. Use Add to choose them."
                />
              </div>

              <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" hidden={tab !== 'items'}>
                <AppDataGrid<QuoteItemRow>
                  headerLeft={childBar(() => setPickItems(true), removeItems, itemSel.size === 0,
                    `quote-${quote.quoteID ?? 'new'}-item-charge`, gridExportMatrix(items, itemColumns))}
                  hideHeaderExport
                  rows={items}
                  columns={itemColumns}
                  getRowId={(r) => r.oid}
                  loading={loading}
                  selectable
                  selectedIds={itemSel}
                  onSelectionChange={setItemSel}
                  onCellCommit={locked ? undefined : commitItemCell}
                  maximizable
                  exportFilename={`quote-${quote.quoteID ?? 'new'}-item-charge`}
                  emptyText="No item charges."
                />
              </div>

              {/* Attachment | Notes — legacy CRMQuotes_Attachments_ListView and
                  CRMQuotes_Note_ListView. The shared documents panel, pointed at
                  this quote and driven by the tab strip above. Its rows save on
                  their own, at once, and are not part of the quote's Save — so
                  they do not mark the form as changed. */}
              <div role="tabpanel" className="sd-childtabpanel qt-tabpanel" data-unsaved-ignore
                   hidden={tab !== 'attachment' && tab !== 'note'}>
                <PreLoginDocsPanel cocOid={id} owner={QUOTE_DOCS} embedded readOnly={locked}
                                   activeTab={tab === 'note' ? 'note' : 'attachment'}
                                   onCounts={setDocCounts} />
              </div>
            </section>
          </div>

          {actionBar(true)}
        </div>
      </div>

      <PriceCodePickerDialog
        open={pickCodes}
        sampleMatrices={lookups?.sampleMatrices ?? []}
        existingKeys={existingRowKeys}
        initialMatrixIds={usedMatrixIds}
        onAdd={addPriceCodes}
        onClose={() => setPickCodes(false)}
      />
      {/* Lookup New / Edit popups — each master's own form. */}
      {/* Client ▸ New — the full Client form, as legacy opens
          Customer_DetailView_SampleRegistration. OK creates the client; it is
          then selected on the quote and Bill To filled from it. */}
      {newClient && (
        <ClientDetailModal clientId={null} notify={notify} zIndex={1600}
          onClose={() => setNewClient(false)}
          onSaved={(newId) => {
            setNewClient(false);
            if (!newId) return;
            void reloadLookups(newId).then((fresh) => {
              const party = fresh?.clients.find((c) => c.id.toLowerCase() === newId.toLowerCase());
              patch({ ...PARTY_RESET, clientId: newId, clientName: party?.name ?? null, ...billFrom(party) });
            });
          }} />
      )}
      {newContact && quote.clientId && (
        <ContactDetailModal clientId={quote.clientId} clientName={quote.clientName ?? ''} type={0} showClientOnNew
          contactOid={null} notify={notify}
          onClose={() => setNewContact(false)}
          onSaved={(savedOid) => {
            setNewContact(false);
            void fetchContacts(quote.clientId, null).then((list) => {
              setContacts(list);
              const c = savedOid ? list.find((x) => x.id.toLowerCase() === savedOid.toLowerCase()) : undefined;
              if (c) patch({
                primaryContactId: c.id, primaryContactName: c.name, emailID: c.email ?? null,
                cellPhone: quote.cellPhone || c.mobilePhone || null,
                officePhone: quote.officePhone || c.officePhone || null,
              });
            });
          }} />
      )}
      <QuoteNamedLookupDialog
        target={namedEdit}
        onClose={() => setNamedEdit(null)}
        onSaved={(savedId) => {
          const kind = namedEdit!.kind;
          setNamedEdit(null);
          void reloadLookups().then(() => patch({ [NAMED_FIELD[kind]]: savedId } as Partial<QuoteDetail>));
        }}
      />
      <TatPickerDialog row={tatRow} onClose={() => setTatRow(null)}
                       onPick={(tatId) => tatRow && pickTat([tatRow.oid], tatId)} />
      <SampleMatrixPickerDialog row={matrixRow} sampleMatrices={lookups?.sampleMatrices ?? []}
                                onClose={() => setMatrixRow(null)}
                                onPick={(smId) => matrixRow && pickSampleMatrix(matrixRow.oid, smId)} />
      {copyMenu && (
        <CellContextMenu
          value={copyMenu.field === 'tatId'
            ? rows.find((r) => r.tatId === copyMenu.value)?.tat ?? copyMenu.value
            : copyMenu.value}
          columnLabel={copyMenu.label}
          selectedCount={copyMenu.grid === 'rows' ? rowSel.size : itemSel.size}
          onCopyAll={() => applyCopy(false)}
          onCopySelected={() => applyCopy(true)}
          position={{ x: copyMenu.x, y: copyMenu.y }}
          onClose={() => setCopyMenu(null)}
        />
      )}
      <QuoteValidateDialog
        open={validating}
        busy={validateBusy}
        onAccept={(won, reason) => void acceptValidate(won, reason)}
        onClose={() => setValidating(false)}
      />
      <ItemChargePickerDialog
        open={pickItems}
        existingIds={existingItemIds}
        onAdd={addItemCharges}
        onClose={() => setPickItems(false)}
      />
    </div>
  );
}

// ── Grid-lookup columns, as legacy's lookup list views show them ─────────────
const CLIENT_COLS  = ['Client', 'Address', 'City', 'ClientCode', 'Phone'];
const PROSPECT_COLS = ['Prospects', 'Topic'];
const PROJECT_COLS = ['Project ID', 'ProjectName', 'ProjectLocation'];
const CONTACT_COLS = ['FullName', 'Street1', 'Phone', 'Email'];
const clientCells   = (c: PartyOption) => [c.name, [c.street1, c.street2].filter(Boolean).join(' '), c.city, c.clientCode, c.phone];
const prospectCells = (c: PartyOption) => [c.name, c.topic];
const projectCells  = (p: ProjectOption) => [p.name, p.projectName, p.projectLocation];
const contactCells  = (c: ContactOption) => [c.name, c.street1, c.officePhone, c.email];

/** The API's error text, when it sent one. */
function errorMessage(e: unknown): string | undefined {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? (e instanceof Error ? e.message : undefined);
}

/**
 * One of the two GLOBAL discount boxes. It holds what is being typed and only
 * hands it over on blur or Enter — committing on every keystroke would re-price
 * the whole quote at "1" on the way to "15". Re-keyed by the parent whenever the
 * server returns a new value, so it always shows the stored number at rest.
 */
function GlobalNumber({ value, onCommit, currency = false, label }: {
  value: number; onCommit: (text: string) => void;
  /** Accessible name — `field()` can only name a plain element, not this one. */
  label: string;
  /** Show "$0.00" at rest, as legacy's money spin editor does. */
  currency?: boolean;
}) {
  const [draft, setDraft] = useState(value.toFixed(2));
  const [editing, setEditing] = useState(false);
  if (currency && !editing) {
    // At rest a money box reads as money; focusing it turns it back into the
    // number spin box to type in.
    return (
      <input type="text" className="sd-input qt-money" value={money.format(Number(draft) || 0)}
             aria-label={label}
             onFocus={() => setEditing(true)} readOnly />
    );
  }
  return (
    <input type="number" step="any" className="sd-input qt-money" value={draft}
           aria-label={label}
           autoFocus={currency}
           onChange={(e) => setDraft(e.target.value)}
           onBlur={() => { setEditing(false); onCommit(draft); }}
           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(draft); } }} />
  );
}

/** A wire date → the `yyyy-MM-dd` an <input type="date"> needs. Goes through the
 *  shared helper, so no date formatting is inlined here (C2).
 *
 *  A value with no zone marker is already a wall-clock date — every value this
 *  form writes back while the user types is one — so its date part is passed
 *  through UNPARSED. Parsing it broke typing the year (user report 2026-10-20):
 *  the first digit gives the input a year of 0002, and JavaScript's Date reads a
 *  year below 100 as 19xx, so the field snapped to 1902 and the next keystroke
 *  could never build 2026. */
function dateInput(v: string | null): string {
  if (!v) return '';
  const naive = /^(\d{4}-\d{2}-\d{2})(T[\d:.]*)?$/.exec(v);
  if (naive?.[1]) return naive[1];
  const [m, d, y] = fmtLocalDate(v).split('/');
  return y && m && d ? `${y}-${m}-${d}` : '';
}

function today(): string {
  const [m, d, y] = fmtLocalDate(new Date().toISOString()).split('/');
  return `${y}-${m}-${d}`;
}
